import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessWebsiteApiService } from "../services/businessWebsiteApiService";
import {
  fromBusinessWebsiteDtoToBusinessWebsite,
  fromUpdateBusinessWebsiteRequestToUpdateBusinessWebsiteDto,
  type BusinessWebsite,
  type UpdateBusinessWebsiteRequest,
} from "../types/businessWebsiteTypes";

export const businessWebsiteKeys = {
  all: ["business-websites"] as const,
  detail: (businessId: string) => [...businessWebsiteKeys.all, businessId] as const,
};

export const useBusinessWebsite = (businessId: string) => useQuery<BusinessWebsite | null, Error>({
  queryKey: businessWebsiteKeys.detail(businessId),
  queryFn: async ({ signal }) => {
    const dto = await businessWebsiteApiService.getByBusinessId(businessId, signal);
    return dto === null ? null : fromBusinessWebsiteDtoToBusinessWebsite(dto);
  },
  enabled: !!businessId,
  staleTime: 5 * 60 * 1000,
});

export const useUpdateBusinessWebsite = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<BusinessWebsite, Error, UpdateBusinessWebsiteRequest>({
    onMutate: () => queryClient.cancelQueries({ queryKey: businessWebsiteKeys.detail(businessId) }),
    mutationFn: async (request) => {
      const dto = fromUpdateBusinessWebsiteRequestToUpdateBusinessWebsiteDto(request);
      const resultDto = await businessWebsiteApiService.update(businessId, dto);
      return fromBusinessWebsiteDtoToBusinessWebsite(resultDto);
    },
    onSuccess: (website) => queryClient.setQueryData(businessWebsiteKeys.detail(businessId), website),
  });
};

export const useUploadWebsiteLogo = (businessId: string) => useMutation({
  mutationFn: (file: File) => businessWebsiteApiService.uploadLogo(businessId, file),
});
